(function () {
  'use strict';

  var FREEZE_THRESHOLD = 500; // ms - consider frozen if frame gap > 500ms
  var CHECK_INTERVAL = 5000; // ms - check DOM/memory every 5s
  var MAX_REPORTS = 3; // max Sentry reports per session
  var reportCount = 0;
  var lastFrameTime = performance.now();
  var pageHidden = false;
  var domHistory = [];
  var longTasks = []; // ring buffer of recent long tasks
  var networkLog = {}; // domain -> count of requests in last 60s
  var networkTimestamps = []; // [{ts, domain}] for sliding window
  var initialHeapMb = 0;

  // Capture baseline heap after page settles
  setTimeout(function () {
    if (performance.memory) {
      initialHeapMb = Math.round(performance.memory.usedJSHeapSize / 1048576);
    }
  }, 5000);

  // --- Track network requests per domain (catches /cart.js spam, Taboola flood) ---
  function logNetwork(url) {
    try {
      var domain = url;
      if (url.indexOf('http') === 0) {
        domain = new URL(url).hostname;
      } else if (url.indexOf('/') === 0) {
        domain = 'neemans.com';
      }
      var now = Date.now();
      networkTimestamps.push({ ts: now, domain: domain });
      networkLog[domain] = (networkLog[domain] || 0) + 1;
      // Expire entries older than 60s
      while (networkTimestamps.length > 0 && now - networkTimestamps[0].ts > 60000) {
        var old = networkTimestamps.shift();
        networkLog[old.domain]--;
        if (networkLog[old.domain] <= 0) delete networkLog[old.domain];
      }
    } catch (e) {}
  }

  // Intercept fetch
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = arguments[0];
      if (typeof url === 'string') logNetwork(url);
      else if (url && url.url) logNetwork(url.url);
      return origFetch.apply(this, arguments);
    };
  }

  // Intercept XHR
  var origXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function () {
    if (arguments[1]) logNetwork(arguments[1]);
    return origXhrOpen.apply(this, arguments);
  };

  // --- Track Long Tasks to find which JS blocks the thread ---
  if (typeof PerformanceObserver === 'function') {
    try {
      var ltObserver = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var attribution = '';
          if (entry.attribution && entry.attribution.length > 0) {
            var attr = entry.attribution[0];
            attribution = attr.containerSrc || attr.containerName || attr.name || 'unknown';
          }
          longTasks.push({
            ts: Math.round(entry.startTime),
            dur: Math.round(entry.duration),
            src: attribution
          });
          if (longTasks.length > 10) longTasks.shift();
        }
      });
      ltObserver.observe({ type: 'longtask', buffered: true });
    } catch (e) {}
  }

  // --- Track page visibility to ignore background tab gaps ---
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      pageHidden = true;
    } else {
      pageHidden = false;
      lastFrameTime = performance.now();
    }
  });

  // --- Frame drop detection (detects unresponsive/white screen) ---
  function monitorFrames() {
    var now = performance.now();
    var delta = now - lastFrameTime;

    if (!pageHidden && delta > FREEZE_THRESHOLD && reportCount < MAX_REPORTS) {
      reportCount++;
      var data = collectDiagnostics(delta);
      sendToSentry('Page freeze detected (' + Math.round(delta) + 'ms)', data);
      console.warn('[NMS Freeze Monitor] Page froze for ' + Math.round(delta) + 'ms', data);
    }

    lastFrameTime = now;
    requestAnimationFrame(monitorFrames);
  }

  // --- DOM node growth tracking (leak indicator) ---
  function trackDOMGrowth() {
    var count = document.getElementsByTagName('*').length;
    domHistory.push({ time: Date.now(), count: count });

    // Keep last 12 entries (1 minute of data)
    if (domHistory.length > 12) {
      domHistory.shift();
    }

    // Alert if DOM grew by more than 500 nodes in last minute
    if (domHistory.length >= 12) {
      var growth = count - domHistory[0].count;
      if (growth > 500 && reportCount < MAX_REPORTS) {
        reportCount++;
        var domSample = sampleDOMNodes();
        sendToSentry('DOM node leak detected (+' + growth + ' nodes in 60s)', {
          current_dom_nodes: count,
          growth_per_minute: growth,
          scroll_position: window.scrollY,
          url: window.location.href,
          network_requests_last_60s: getTopNetworkDomains(),
          heaviest_sections: domSample.heaviest_sections,
          tag_breakdown: domSample.tag_breakdown,
          time_since_nav_ms: Math.round(performance.now())
        });
        console.warn('[NMS Freeze Monitor] DOM growing fast: +' + growth + ' nodes/min. Total: ' + count);
      }
    }
  }

  // --- Collect diagnostics snapshot ---
  function collectDiagnostics(freezeDuration) {
    // Find resources that loaded during/just before the freeze window
    var freezeEnd = performance.now();
    var freezeStart = freezeEnd - freezeDuration;
    var recentResources = [];
    try {
      var resources = performance.getEntriesByType('resource');
      for (var r = resources.length - 1; r >= 0 && recentResources.length < 8; r--) {
        var res = resources[r];
        // Resources active during the freeze window
        if (res.responseEnd >= freezeStart - 500 && res.startTime <= freezeEnd) {
          var name = res.name;
          // Shorten URL: keep domain + last path segment
          try {
            var u = new URL(name);
            var parts = u.pathname.split('/');
            name = u.hostname + '/.../' + parts[parts.length - 1];
          } catch (e) {}
          recentResources.push({
            name: name,
            type: res.initiatorType,
            dur: Math.round(res.duration),
            size: res.transferSize || 0
          });
        }
      }
    } catch (e) {}

    var imgs = document.getElementsByTagName('img');
    var pendingImages = 0;
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) pendingImages++;
    }

    var data = {
      freeze_duration_ms: Math.round(freezeDuration),
      dom_nodes: document.getElementsByTagName('*').length,
      scroll_position: window.scrollY,
      viewport_height: window.innerHeight,
      page_height: document.documentElement.scrollHeight,
      url: window.location.href,
      total_images: imgs.length,
      pending_images: pendingImages,
      time_since_nav_ms: Math.round(performance.now()),
      long_tasks_before_freeze: longTasks.slice(),
      resources_during_freeze: recentResources,
      network_requests_last_60s: getTopNetworkDomains()
    };

    // Chrome-only: memory info
    if (performance.memory) {
      data.js_heap_used_mb = Math.round(performance.memory.usedJSHeapSize / 1048576);
      data.js_heap_total_mb = Math.round(performance.memory.totalJSHeapSize / 1048576);
      data.js_heap_growth_mb = data.js_heap_used_mb - initialHeapMb;
    }

    // Script sizes by domain — shows which third-party JS is heaviest
    data.script_sizes = getScriptSizesByDomain();

    return data;
  }

  // --- Get top network domains by request count ---
  function getTopNetworkDomains() {
    var sorted = Object.keys(networkLog).map(function (d) {
      return { domain: d, count: networkLog[d] };
    }).sort(function (a, b) { return b.count - a.count; });
    return sorted.slice(0, 10);
  }

  // --- Sample DOM to find what's growing ---
  function sampleDOMNodes() {
    var tagCounts = {};
    var heavyParents = {};
    var all = document.body.children;
    for (var i = 0; i < all.length; i++) {
      var child = all[i];
      var tag = child.tagName.toLowerCase();
      var childCount = child.querySelectorAll('*').length;
      tagCounts[tag] = (tagCounts[tag] || 0) + childCount;
      // Find sections with most children
      var id = child.id || child.className.toString().split(' ')[0] || tag;
      if (childCount > 50) {
        heavyParents[id] = childCount;
      }
    }
    // Sort heavy parents
    var sorted = Object.keys(heavyParents).map(function (k) {
      return { el: k, nodes: heavyParents[k] };
    }).sort(function (a, b) { return b.nodes - a.nodes; });
    return { tag_breakdown: tagCounts, heaviest_sections: sorted.slice(0, 5) };
  }

  // --- Get JS payload sizes by domain ---
  function getScriptSizesByDomain() {
    var domainSizes = {};
    try {
      var resources = performance.getEntriesByType('resource');
      for (var i = 0; i < resources.length; i++) {
        var res = resources[i];
        if (res.initiatorType === 'script' && res.transferSize > 0) {
          var domain;
          try { domain = new URL(res.name).hostname; } catch (e) { domain = 'unknown'; }
          if (!domainSizes[domain]) domainSizes[domain] = 0;
          domainSizes[domain] += res.transferSize;
        }
      }
    } catch (e) {}
    // Sort by size, return top 10
    return Object.keys(domainSizes).map(function (d) {
      return { domain: d, kb: Math.round(domainSizes[d] / 1024) };
    }).sort(function (a, b) { return b.kb - a.kb; }).slice(0, 10);
  }

  // --- Send to Sentry ---
  function sendToSentry(message, data) {
    if (window.Sentry && typeof window.Sentry.captureMessage === 'function') {
      window.Sentry.captureMessage(message, {
        level: 'warning',
        tags: {
          monitor: 'nms-freeze-monitor',
          page_type: document.body.getAttribute('data-page-type') || 'unknown'
        },
        extra: data
      });
    }
  }

  // --- Start monitoring ---
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(monitorFrames);
  }
  setInterval(trackDOMGrowth, CHECK_INTERVAL);
})();
